import React, { useEffect, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography } from 'antd';
import { store } from "../store/store.js";
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    DashboardOutlined,
    UserOutlined,
    SettingOutlined,
    LogoutOutlined,
    BellOutlined,
    TruckFilled,
    AppstoreOutlined,
    CalculatorOutlined,
    AppstoreFilled,
    MessageOutlined,
    FieldTimeOutlined,
    NotificationFilled,
    DollarCircleFilled,
    FileDoneOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from "../store/slices/authSlice";
import { hasPermission } from '../store/slices/roleSlice.js';
import { useSelector } from 'react-redux';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

const AdminLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [openKeys, setOpenKeys] = useState([]);
    const user = useSelector((state) => state?.auth?.after_login_data?.data)

    useEffect(() => {
        if (
            location.pathname.startsWith('/categories') ||
            location.pathname.startsWith('/services') ||
            location.pathname.startsWith('/subservices')
        ) {
            setOpenKeys(['master']);
        } else {
            setOpenKeys([]);
        }
    }, [location.pathname]);

    // Menu items configuration
    const menuItems = [
        {
            key: '/dashboard',
            icon: <DashboardOutlined />,
            label: 'Dashboard',
        },
        {
            key: '/agents',
            icon: <UserOutlined />,
            label: 'Agents',
            permission: "view_agent"
        },
        {
            key: '/customers',
            icon: <UserOutlined />,
            label: 'Customers',
            permission: "view_customer"
        },
        {
            key: '/b2busers',
            icon: <UserOutlined />,
            label: 'B2B Users',
            permission: "view_customer"
        },
        {
            key: '/trucks',
            icon: <TruckFilled />,
            label: 'Trucks',
            permission: "view_truck"
        },
        // {
        //     key: '/categories',
        //     icon: <AppstoreOutlined />,
        //     label: 'Categories',
        // },
        {
            key: '/jobs',
            icon: <AppstoreOutlined />,
            label: 'Jobs',
            permission: "view_job"
        },
        {
            key: '/codes',
            icon: <AppstoreOutlined />,
            label: 'Codes',
            permission: "view_code"
        },
        {
            key: '/time_slot',
            icon: <FieldTimeOutlined />,
            label: 'Time Slot',
        },
        {
            key: '/role',
            icon: <UserOutlined />,
            label: 'Role',
            permission: "view_role"
        },
        {
            key: '/sub_admin',
            icon: <UserOutlined />,
            label: 'Sub Admin',
            permission: "view_subadmin"
        },
        // {
        //     key: '/services',
        //     icon: <AppstoreFilled />,
        //     label: 'Services',
        // },
        {
            key: 'master',
            icon: <AppstoreOutlined />,
            label: 'Master Data',
            children: [
                {
                    key: '/categories',
                    icon: <AppstoreOutlined />,
                    label: 'Categories',
                },
                {
                    key: '/services',
                    icon: <AppstoreFilled />,
                    label: 'Services',
                },
            ],
        },
        {
            key: '/notification',
            icon: <NotificationFilled />,
            label: 'Notification',
            permission: "view_notification"
        },
        {
            key: 'promotion',
            icon: <AppstoreOutlined />,
            label: 'Promotions',
            children: [
                {
                    key: '/coupon',
                    icon: <AppstoreOutlined />,
                    label: 'Coupon Management',
                    permission: 'view_coupon',
                },
                {
                    key: '/promotion',
                    icon: <AppstoreFilled />,
                    label: 'Promotional Offer',
                    permission: 'view_promotion',
                },
            ],
        },
        {
            key: '/chat-question',
            icon: <MessageOutlined />,
            label: 'Chat Questions',
        },
        {
            key: '/invoice',
            icon: <SettingOutlined />,
            label: 'Invoice',
        },
        {
            key: '/transaction',
            icon: <DollarCircleFilled />,
            label: 'Transactions',
            permission: "view_transaction"
        },
        {
            key: '/reviews',
            icon: <FileDoneOutlined />,
            label: 'Reviews',
            permission: "view_review"
        },
        {
            key: '/settings',
            icon: <SettingOutlined />,
            label: 'Settings',
            permission: "view_setting"
        },
        {
            key: '/banners',
            icon: <SettingOutlined />,
            label: 'Banners',
        },
        {
            key: '/chatbotcustomers',
            icon: <UserOutlined />,
            label: 'Chat Bot Customers',
        }
    ];

    const filteredMenu = menuItems
        .map((item) => {
            // If item has children → filter them first
            if (item.children?.length) {
                const filteredChildren = item.children.filter((child) => {
                    if (child.permission) {
                        return hasPermission(user, child.permission);
                    }
                    return true;
                });

                // If no children left after filtering → remove parent
                if (filteredChildren.length === 0) return null;

                return {
                    ...item,
                    children: filteredChildren,
                };
            }

            // If single item with permission
            if (item.permission) {
                return hasPermission(user, item.permission) ? item : null;
            }

            return item;
        })
    .filter(Boolean);

    // Handle menu click
    const handleMenuClick = ({ key }) => {
        navigate(key);
    };

    const handleOpenChange = (keys) => {
        setOpenKeys(keys);
    };
    

    // User dropdown menu
    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            danger: true,
        },
    ];

    const handleUserMenuClick = ({ key }) => {
        if (key === 'logout') {
            store.dispatch(logout());
            navigate("/");
            console.log('Logout clicked');
        } else if (key === 'profile') {
            // Handle profile navigation
            console.log('Profile clicked');
        }
    };

    const getSelectedKey = (path) => {
        if (path.startsWith('/jobs') || path.startsWith('/job')) return '/jobs';
        if (path.startsWith('/agents') || path.startsWith('/agent')) return '/agents';
        if (path.startsWith('/customerchat')) return '/customerchat';
        if (path.startsWith('/customers') || path.startsWith('/customer')) return '/customers';
        if (path.startsWith('/trucks') || path.startsWith('/truck')) return '/trucks';
        if (path.startsWith('/codes') || path.startsWith('/code')) return '/codes';
        if (path.startsWith('/categories')) return '/categories';
        if (path.startsWith('/time_slot')) return '/time_slot';
        if (path.startsWith('/services') || path.startsWith('/subservices')) return '/services';
        if (path.startsWith('/chat-question')) return '/chat-question';
        return path;
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                trigger={null}
                collapsible
                collapsed={collapsed}
                theme="dark"
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    background: '#2C323A',
                }}
            >
                <div
                    style={{
                        height: 64,
                        margin: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: collapsed ? 20 : 20,
                        fontWeight: 'bold',
                        color: '#B49630',
                    }}
                >
                    {collapsed ? 'FA' : 'Furnifix Admin'}
                </div>
                <Menu
                    theme="dark"
                    mode="inline"
                    // selectedKeys={[location.pathname]}
                    selectedKeys={[getSelectedKey(location.pathname)]}
                    // openKeys={[getSelectedKey(location.pathname)]}
                    openKeys={openKeys}
                    onOpenChange={handleOpenChange}
                    defaultOpenKeys={location.pathname.startsWith('/settings') ? ['/settings'] : []}
                    items={filteredMenu}
                    onClick={handleMenuClick}
                />
            </Sider>
            <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
                <Header
                    style={{
                        padding: '0 24px',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                >
                    <div
                        style={{
                            fontSize: 18,
                            cursor: 'pointer',
                            padding: '0 12px',
                        }}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </div>
                    <Space size="large">
                        <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
                        <Dropdown
                            menu={{
                                items: userMenuItems,
                                onClick: handleUserMenuClick,
                            }}
                            placement="bottomRight"
                        >
                            <Space style={{ cursor: 'pointer' }}>
                                <Avatar icon={<UserOutlined />} />
                                <Text>Admin User</Text>
                            </Space>
                        </Dropdown>
                    </Space>
                </Header>
                <Content
                    style={{
                        margin: '24px 16px',
                        padding: 24,
                        minHeight: 280,
                        background: '#fff',
                        borderRadius: 8,
                    }}
                >
                    {children}
                </Content>
                <Footer
                    style={{
                        textAlign: 'center',
                        background: '#fff',
                    }}
                >
                    Furnifix Admin ©2025
                </Footer>
            </Layout>
        </Layout>
    );
};

export default AdminLayout;

